import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { contractsAPI, customersAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Copy, Eye, Trash2, CheckCircle2, Clock, Send, FileText } from 'lucide-react';

const DEFAULT_TERMS = `SERVICE AGREEMENT

This Service Agreement ("Agreement") is entered into between XA's Lawns ("Company") and the Customer identified below.

SERVICES: The Company agrees to provide lawn care services as described in this contract at the service address specified.

PAYMENT: Customer agrees to pay the agreed rate for services rendered. Payment is due within 7 days of service completion unless otherwise arranged.

SCHEDULING: Services will be performed on a regular schedule based on the billing frequency specified. The Company reserves the right to reschedule due to weather conditions.

CANCELLATION: Either party may terminate this agreement with 14 days written notice.

LIABILITY: The Company is insured for property damage and will make reasonable efforts to avoid damage. Customer agrees to remove or secure items that may be damaged by lawn care equipment.

ACCEPTANCE: By accepting this contract, you agree to the terms above and authorize XA's Lawns to perform lawn care services at your property.`;

export default function Contracts() {
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    service_address: '',
    services_description: 'Lawn mowing, edging, and cleanup',
    rate: '',
    billing_frequency: 'per-cut',
    contract_start_date: '',
    contract_end_date: '',
    terms: DEFAULT_TERMS,
    customer_notes: ''
  });

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [contractsRes, customersRes] = await Promise.all([
        contractsAPI.getAll(params),
        customersAPI.getAll()
      ]);
      
      setContracts(contractsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    
    if (customer) {
      const fullAddress = [
        customer.street_address,
        customer.city,
        customer.state,
        customer.zip_code
      ].filter(Boolean).join(', ');
      
      setFormData({
        ...formData,
        customer_id: customerId,
        service_address: fullAddress,
        rate: customer.cost_per_service?.toString() || ''
      });
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.rate || !formData.contract_start_date || !formData.terms) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await contractsAPI.create({
        ...formData,
        rate: parseFloat(formData.rate),
        contract_end_date: formData.contract_end_date || null,
        customer_notes: formData.customer_notes || null
      });
      
      toast.success('Contract created! Copy the link to share with customer.');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
      
      // Open view dialog to show the link
      setSelectedContract(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      service_address: '',
      services_description: 'Lawn mowing, edging, and cleanup',
      rate: '',
      billing_frequency: 'per-cut',
      contract_start_date: '',
      contract_end_date: '',
      terms: DEFAULT_TERMS,
      customer_notes: ''
    });
    setSelectedCustomer(null);
  };

  const copyContractLink = (token) => {
    const link = `${baseUrl}/contract/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Contract link copied to clipboard!');
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    
    try {
      await contractsAPI.delete(contractId);
      toast.success('Contract deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'viewed':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'sent':
        return <Send className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'accepted': 'bg-green-100 text-green-800',
      'viewed': 'bg-blue-100 text-blue-800',
      'sent': 'bg-yellow-100 text-yellow-800',
      'draft': 'bg-gray-100 text-gray-800',
      'expired': 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getBillingLabel = (frequency) => {
    const labels = {
      'per-cut': 'Per Cut',
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-Weekly',
      'monthly': 'Monthly'
    };
    return labels[frequency] || frequency;
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contracts</h1>
          <p className="text-gray-500 mt-1">Manage service agreements</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="create-contract-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Contract
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Contract</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateContract} className="space-y-4 mt-4">
              <div>
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={handleCustomerSelect}>
                  <SelectTrigger data-testid="contract-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.filter(c => c.status === 'active').map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.contact_name} - {c.phone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Service Address *</Label>
                <Input
                  value={formData.service_address}
                  onChange={(e) => setFormData({...formData, service_address: e.target.value})}
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              <div>
                <Label>Services Description</Label>
                <Input
                  value={formData.services_description}
                  onChange={(e) => setFormData({...formData, services_description: e.target.value})}
                  placeholder="Lawn mowing, edging, and cleanup"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rate *</Label>
                  <Input
                    type="number"
                    value={formData.rate}
                    onChange={(e) => setFormData({...formData, rate: e.target.value})}
                    placeholder="50"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label>Billing Frequency</Label>
                  <Select value={formData.billing_frequency} onValueChange={(v) => setFormData({...formData, billing_frequency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per-cut">Per Cut</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contract Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.contract_start_date}
                    onChange={(e) => setFormData({...formData, contract_start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Contract End Date</Label>
                  <Input
                    type="date"
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({...formData, contract_end_date: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Customer Notes (Visible to Customer)</Label>
                <Textarea
                  value={formData.customer_notes}
                  onChange={(e) => setFormData({...formData, customer_notes: e.target.value})}
                  placeholder="Any special notes for this customer..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Contract Terms *</Label>
                <Textarea
                  value={formData.terms}
                  onChange={(e) => setFormData({...formData, terms: e.target.value})}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black">
                  Create Contract
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="viewed">Viewed</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contracts List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : contracts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No contracts found</p>
            <p className="text-sm text-gray-400 mt-2">Create a contract to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {contracts.map((contract) => (
            <Card key={contract.id} className="border-0 shadow-sm" data-testid={`contract-card-${contract.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(contract.status)}
                      <h3 className="font-semibold text-lg text-gray-900">{contract.customer_name}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(contract.status)}`}>
                        {contract.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{contract.service_address}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span><strong>Rate:</strong> ${contract.rate} ({getBillingLabel(contract.billing_frequency)})</span>
                      <span><strong>Start:</strong> {contract.contract_start_date}</span>
                      {contract.contract_end_date && <span><strong>End:</strong> {contract.contract_end_date}</span>}
                    </div>
                    
                    {/* Timestamps */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-400 mt-2">
                      {contract.sent_at && <span>Sent: {new Date(contract.sent_at).toLocaleString()}</span>}
                      {contract.viewed_at && <span>Viewed: {new Date(contract.viewed_at).toLocaleString()}</span>}
                      {contract.accepted_at && <span className="text-green-600">Accepted: {new Date(contract.accepted_at).toLocaleString()}</span>}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedContract(contract);
                        setViewDialogOpen(true);
                      }}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      className="bg-lime-500 hover:bg-lime-600 text-black"
                      onClick={() => copyContractLink(contract.token)}
                      title="Copy Link"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    {contract.status !== 'accepted' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(contract.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Contract Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contract Details</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">{selectedContract.customer_name}</h2>
                  <p className="text-gray-500">{selectedContract.customer_phone}</p>
                </div>
                <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusBadge(selectedContract.status)}`}>
                  {selectedContract.status.toUpperCase()}
                </span>
              </div>

              {/* Contract Link */}
              <div className="p-4 bg-lime-50 border border-lime-200 rounded-lg">
                <Label className="text-lime-700 font-semibold">Share this link with your customer:</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={`${baseUrl}/contract/${selectedContract.token}`}
                    readOnly
                    className="bg-white"
                  />
                  <Button
                    className="bg-lime-500 hover:bg-lime-600 text-black"
                    onClick={() => copyContractLink(selectedContract.token)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-lime-600 mt-2">
                  Copy this link and send it to your customer via text message or email
                </p>
              </div>

              {/* Contract Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Service Address</p>
                  <p className="font-medium">{selectedContract.service_address}</p>
                </div>
                <div>
                  <p className="text-gray-500">Rate</p>
                  <p className="font-medium">${selectedContract.rate} ({getBillingLabel(selectedContract.billing_frequency)})</p>
                </div>
                <div>
                  <p className="text-gray-500">Start Date</p>
                  <p className="font-medium">{selectedContract.contract_start_date}</p>
                </div>
                <div>
                  <p className="text-gray-500">End Date</p>
                  <p className="font-medium">{selectedContract.contract_end_date || 'Ongoing'}</p>
                </div>
              </div>

              {/* Timestamps */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Activity Log</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Contract Created:</span>
                    <span>{new Date(selectedContract.created_at).toLocaleString()}</span>
                  </div>
                  {selectedContract.sent_at && (
                    <div className="flex justify-between">
                      <span>Sent:</span>
                      <span>{new Date(selectedContract.sent_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedContract.viewed_at && (
                    <div className="flex justify-between text-blue-600">
                      <span>Viewed by Customer:</span>
                      <span>{new Date(selectedContract.viewed_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedContract.accepted_at && (
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>✓ Accepted:</span>
                      <span>{new Date(selectedContract.accepted_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedContract.accepted_from_ip && (
                    <div className="flex justify-between text-gray-400 text-xs">
                      <span>IP Address:</span>
                      <span>{selectedContract.accepted_from_ip}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
